/*************************************************************************
 * @license
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/* eslint import/extensions: 0 */

/* global window:readonly, document:readonly, navigator:readonly */

import { getBundleVersion } from './util/bundleAssetUrl';

window.App = window.App || {};
const App = window.App;

App.Env = App.Env || {};
const Env = App.Env;

App.Env.plugins = App.Env.plugins || {};
const { AdobeIMS } = require('@identity/imslib');
const pkg = require('../package');
const IMS_AUTH_SCOPE = require('./constants/imsConstants').default;

let loggedInState;

const environment = {
    DEV: 'dev',
    STAGE: 'stage',
    PROD: 'prod',
};

const DEEP_RESEARCH_DOMAIN = 'deep-research-dev.corp.ethos874-stage-va6.ethos.adobe.net';
const DEEP_RESEARCH_URL = `https://${DEEP_RESEARCH_DOMAIN}/deepresearch/`;

/**
 * Derive the app base URL from the current document location. The same bundle
 * works whether the document was loaded from "/", "/entai-cdn/", or any other
 * prefix because we strip an HTML filename, but preserve directory names:
 *   "/entai-cdn/index.html" -> "/entai-cdn"
 *   "/entai-cdn/"           -> "/entai-cdn"
 *   "/index.html" or "/"    -> ""
 */
const getAppBaseUrl = ({ origin, pathname }) => {
    const basePath = pathname.replace(/\/[^/]*\.html?$/i, '').replace(/\/$/, '');
    return `${origin}${basePath}`;
};

/**
 * Derive domain configuration from window.location when not provided by launcher
 */
const deriveDomainsFromLocation = (location = window.location) => {
    const { origin, hostname, pathname } = location;
    const urlParams = new URLSearchParams(location.search);

    // CDN prefix (if any) is taken from the path the document was served from,
    // so any host can serve the app from any path without code changes.
    const cdnDomain = getAppBaseUrl(location);

    const isAcrobatHost = hostname === 'acrobat.adobe.com' || hostname.endsWith('.acrobat.adobe.com');
    const appPath = isAcrobatHost && pathname.match(/^\/(knowledgebase(?:-dev|-stage)?)(?:\/|$)/);
    const apiDomain = appPath
        ? `https://${appPath[1]}.adobe.io`
        : origin.replace(/\.adobe\.com(?=:|$)/, '.adobe.io');

    // Determine environment from hostname
    const isDev = hostname.includes('-dev.') || hostname.startsWith('dev.')
        || hostname.includes('localhost') || hostname.includes('preview.')
        || appPath?.[1] === 'knowledgebase-dev';
    const isStage = hostname.includes('stage.') || appPath?.[1] === 'knowledgebase-stage';
    let env = environment.PROD;
    if (isDev) {
        env = environment.DEV;
    } else if (isStage) {
        env = environment.STAGE;
    }

    const isProd = !(isDev || isStage);
    // Locale priority: explicit URL override, then the browser's locale, then en-US.
    // This is used to localize the pre-auth Adobe IMS login page (see imsLocale below),
    // which renders before our own app/profile locale resolution has run.
    const browserLocale = (typeof navigator !== 'undefined'
        && (navigator.languages?.[0] || navigator.language)) || '';
    const locale = urlParams.get('locale') || browserLocale || 'en-US';

    return { cdnDomain, apiDomain, env, isDev, isStage, isProd, locale };
};

/**
 * Inject CSP meta tag dynamically based on environment
 */
const injectCSPMetaTag = (isDev, isStage, cdnHost, apiHost) => {
    const apiWildcard = `*.${apiHost}`;

    // Environment-specific domains for CSP
    const aceCdnHost = isDev
        ? 'shared-components.stage.adobe.com shared-components.adobe.com'
        : 'shared-components.adobe.com';
    const adobeCcCdn = isDev ? 'stage.adobeccstatic.com prod.adobeccstatic.com' : 'prod.adobeccstatic.com';
    const clientMessagingCdn = isDev ? 'stage-client.messaging.adobe.com' : 'client.messaging.adobe.com';
    const serverMessagingCdn = isDev ? 'stage-server.messaging.adobe.com' : 'server.messaging.adobe.com';
    const uiMessagingCdn = isDev ? 'stage-ui.messaging.adobe.com' : 'ui.messaging.adobe.com';
    const pandoraCdnHost = isDev ? 'pandora-cdn.stage.adobe.com' : 'pandora-cdn.adobe.com';
    const ppsHost = isDev ? 'pps-stage.adobe.io' : 'pps.adobe.io';
    const ppsServices = isDev ? 'pps-stage.services.adobe.com' : 'pps.services.adobe.com';
    const p13nHost = isDev ? 'p13n-stage.adobe.io' : 'p13n.adobe.io';
    const imsHost = isDev ? 'adobeid-na1-stg1.services.adobe.com' : 'adobeid-na1.services.adobe.com';
    const serviceAccountHost = isDev ? 'service.stage.account.adobe.com' : 'service.account.adobe.com';
    const adobeImsHost = isDev ? 'ims-na1-stg1.adobelogin.com' : 'ims-na1.adobelogin.com';
    const deepResearchHost = isDev ? DEEP_RESEARCH_DOMAIN : '';

    // Build CSP policy (note: frame-ancestors and report-uri not supported in meta tags)
    const styleSrc = [
        "'self'", "'unsafe-inline'", 'p.typekit.net', 'use.typekit.net',
        cdnHost, apiHost, adobeCcCdn, clientMessagingCdn, pandoraCdnHost, 'bam.nr-data.net',
    ].join(' ');

    const connectSrc = [
        "'self'", 'p.typekit.net', 'use.typekit.net', adobeImsHost, cdnHost, apiHost, apiWildcard,
        aceCdnHost, adobeCcCdn, clientMessagingCdn, serverMessagingCdn, serviceAccountHost,
        p13nHost, ppsHost, imsHost, 'odin.adobe.com', 'js.live.net', 'js-agent.newrelic.com',
        'bam.nr-data.net', 'graph.microsoft.com', 'docs.google.com', 'apis.google.com',
        'accounts.google.com', 'www.googleapis.com',
        // Deep Research's pictures, fetched with KB's access token once its sign-in is on (dev only, like its frames).
        deepResearchHost,
    ].join(' ');

    const scriptSrc = [
        "'self'", "'unsafe-eval'", '*.aptrinsic.com', 'p.typekit.net',
        'use.typekit.net', cdnHost, apiHost, aceCdnHost, adobeCcCdn, clientMessagingCdn,
        pandoraCdnHost, 'odin.adobe.com', 'js.live.net', 'js-agent.newrelic.com', 'bam.nr-data.net',
        'docs.google.com', 'apis.google.com', 'accounts.google.com',
    ].join(' ');

    const frameSrc = [
        "'self'",
        cdnHost,
        uiMessagingCdn,
        '*.adobe.com',
        '*.acrs.adobe.com',
        'acrs.adobe.com',
        deepResearchHost,
        'apis.google.com',
        'docs.google.com',
        'accounts.google.com',
    ].join(' ');

    const imgSrc = [
        "'self'", 'data:', 'blob:', '*.typekit.net', cdnHost, apiHost, ppsServices,
        adobeCcCdn, 's7d2.scene7.com',
        // Favicons: www.google.com/s2/favicons 301s to t{0-3}.gstatic.com and
        // CSP re-checks the redirect target. Deployed builds use the Akamai
        // header in static.echocdn.com; mirror changes there.
        'www.google.com', '*.gstatic.com',
        // Deep Research's thumbnails and pictures (the chat's job cards, Creations), dev only like its frames.
        deepResearchHost,
    ].join(' ');

    const csp = [
        "base-uri 'self'",
        'block-all-mixed-content',
        "default-src 'self'",
        "media-src 'self'",
        "manifest-src 'self'",
        `style-src ${styleSrc}`,
        `connect-src ${connectSrc}`,
        `script-src ${scriptSrc}`,
        "worker-src 'self' blob:",
        `frame-src ${frameSrc}`,
        `img-src ${imgSrc}`,
        `font-src 'self' data: ${cdnHost} *.typekit.net`,
        "child-src 'self'",
    ].join('; ');

    // Only inject if not already present
    if (!document.querySelector('meta[http-equiv="Content-Security-Policy"]')) {
        const cspMeta = document.createElement('meta');
        cspMeta.httpEquiv = 'Content-Security-Policy';
        cspMeta.content = csp;
        document.head.appendChild(cspMeta);
    }
};

/**
 * Make an augmented ready() Promise that can be resolved or rejected
 * by ready.resolve() & ready.reject()
 *
 * @return {Promise} The augmented Promise
 */
const makeReady = () => {
    let promiseMethods = {};

    const _promise = new Promise((resolve, reject) => {
        promiseMethods = { resolve, reject };
    });
    const ready = () => _promise;
    Object.assign(ready, promiseMethods);
    return ready;
};

/**
 * Generates Env.plugins.<plugin>.
 *
 * This should be called first. Once defined, subsequently the Env can
 * be used as:
 *
 *   import { Env } from '../env'
 *
 * In hosted environments, App.env will be defined by the launcher - in index.html
 * ref: https://git.corp.adobe.com/Colosseum/lexabyte/blob/trunk/src/lexabyte/resources/templates/index.html
 *
 * @param pluginName {string} Name of plugin/entry point
 * @return {Object}
 */
const getPluginEnv = () => {
    const pluginName = 'entai';
    App.Env.plugins[pluginName] = App.Env.plugins[pluginName] || {};
    const plugin = App.Env.plugins[pluginName];

    Object.assign(plugin, {
        pluginName,
        version: getBundleVersion() ?? `${pkg.version}`,
    });

    // Use defineProperty() since getter/setter can't be assigned().
    // handles case for both DC (window.adobeIMS) or sign (Env.user)
    // Use a getter here so that it updates after test-harness login
    Object.defineProperty(plugin, 'loggedIn', {
        get() {
            return loggedInState !== undefined
                ? loggedInState
                : !!window.adobeIMS || (Env.user && Env.user.loggedIn);
        },
        set(state) {
            loggedInState = state;
        },
        configurable: true, // allows redefinition (in case of reloading plugin)
    });

    plugin.isIframed = window.self !== window.parent;
    plugin.isTestHarness = window.location.hostname === 'localhost'
        || window.location.hostname === '127.0.0.1'
        || window.location.hostname === 'localhost.adobe.com';

    // Add ready (note launcher may already add this)
    plugin.ready = plugin.ready || makeReady();
    const version = plugin.isTestHarness ? '__VERSION__' : plugin.version;

    // Derive domains from location if not already set (standalone CDN mode)
    const derivedConfig = deriveDomainsFromLocation();
    const assetDomain = plugin.assetBaseUrl || derivedConfig.cdnDomain;
    const isFromCDN = !plugin.domain && !plugin.isTestHarness;
    if (plugin.isTestHarness) {
        // Allow override via environment variable for local development
        // Set domain to origin (+ any path prefix) if not already set to avoid
        // duplicate __VERSION__ in paths. Honoring the prefix lets us serve the
        // app from e.g. /entai-cdn/ locally to mirror the deployed CDN layout.
        if (!plugin.domain) {
            plugin.domain = assetDomain;
        }
        plugin.i18nDomain = assetDomain;
        plugin.apiDomain = process.env.APP_API_DOMAIN || 'https://knowledgebase-dev.adobe.io';
        plugin.inMaintenance = 'false';
        plugin.embedClientId = 'a333378b7e1e414d9783c2a220d07d2d';
        plugin.imsClientId = 'entai-ui-ims-stage';
        plugin.inMaintenance = 'false';

        // Inline CSP is only needed for the local debug harness, which has no
        // server in front of it to set the real CSP response header. In deployments,
        // CSP is returned in the HTTP response header, set up through Akamai in
        // https://git.corp.adobe.com/EchoSign/static.echocdn.com
        const cdnHost = window.location.hostname;
        const apiUrl = new URL(plugin.apiDomain);
        const apiHost = apiUrl.port ? `${apiUrl.hostname}:${apiUrl.port}` : apiUrl.hostname;
        injectCSPMetaTag(derivedConfig.isDev, derivedConfig.isStage, cdnHost, apiHost);
    } else if (isFromCDN) { // This will temporarily either be served directly from CDN or from the lexabyte server
        // Standalone mode: derive all config from window.location
        const isProd = !derivedConfig.isDev && !derivedConfig.isStage;
        plugin.domain = assetDomain;
        plugin.i18nDomain = assetDomain;
        plugin.apiDomain = derivedConfig.apiDomain;
        plugin.environment = derivedConfig.env;
        plugin.inMaintenance = 'false';
        plugin.imsClientId = isProd ? 'entai-ui-ims-prod' : 'entai-ui-ims-stage';
        plugin.embedClientId = isProd
            ? 'e00fe6f63cf349dfb9f90f3c0a2c6ca9'
            : 'a333378b7e1e414d9783c2a220d07d2d';
        Env.locale = derivedConfig.locale;
    }
    if (plugin.assetBaseUrl) {
        plugin.domain = assetDomain;
        plugin.i18nDomain = assetDomain;
    }
    if (plugin.isTestHarness || plugin.domain.toString().includes('localhost')) {
        plugin.environment = environment.DEV;
        plugin.reportEmail = 'your_ldap@adobe.com';
    } else {
        if (!plugin.environment) {
            plugin.environment = deriveDomainsFromLocation(new URL(plugin.domain)).env;
        }
        plugin.reportEmail = {
            [environment.DEV]: 'entai-trust-dev@adobe.com',
            [environment.STAGE]: 'entai-trust-stage@adobe.com',
            [environment.PROD]: 'entai-trust@adobe.com',
        }[plugin.environment];
    }
    plugin.adobeOrgId = plugin.environment === environment.DEV
        ? '90FC331D59DBA35E0A494204@AdobeOrg'
        : 'EE9332B3547CC74E0A4C98A1@AdobeOrg';
    plugin.imsEnvironment = ['stage', 'prod'].includes(plugin.environment) ? undefined : 'stg1';

    // Initialize IMS authentication for all environments
    // This ensures IMS is ready before i18n is initialized in index.js
    // IMPORTANT: This is the ONLY place IMS should be initialized to avoid race conditions
    // where the first instance parses the hash token and strips it before React mounts
    const imsLocale = (Env.locale || derivedConfig.locale || 'en-US').replace('-', '_');

    // Auth bridge: queue for React to consume auth events that happen before mount
    plugin._authQueue = [];
    plugin._authCallbacks = [];

    // Register a callback for auth events (called by Authentication.js on mount)
    plugin.onAuthEvent = callback => {
        plugin._authCallbacks.push(callback);
        // If auth already happened before React mounted, replay it
        if (plugin._pendingAuth) {
            callback(plugin._pendingAuth);
        }
    };

    // Unregister callback (for cleanup)
    plugin.offAuthEvent = callback => {
        plugin._authCallbacks = plugin._authCallbacks.filter(cb => cb !== callback);
    };

    const imsConfig = {
        client_id: plugin.imsClientId,
        locale: imsLocale,
        environment: plugin.imsEnvironment,
        scope: IMS_AUTH_SCOPE,
        onReady: () => {
            plugin.imsReady = true;
            // Notify any registered callbacks
            plugin._authCallbacks.forEach(cb => cb({ type: 'ready', isSignedIn: window.adobeIms?.isSignedInUser() }));
        },
        onAccessToken: tokenInformation => {
            // Get profile and store for React to consume
            window.adobeIms.getProfile().then(profile => {
                const authData = {
                    type: 'token',
                    token: tokenInformation.token,
                    expire: tokenInformation.expire,
                    profile,
                };
                plugin._pendingAuth = authData;
                // Notify any registered callbacks (if React has mounted)
                plugin._authCallbacks.forEach(cb => cb(authData));
            }).catch(error => {
                console.error('env ims getProfile error:', error);
            });
        },
        onAccessTokenHasExpired: () => {
            plugin._pendingAuth = null;
            plugin._authCallbacks.forEach(cb => cb({ type: 'expired' }));
        },
        onError: error => {
            console.error('AdobeIMS initialization error:', error);
            plugin._authCallbacks.forEach(cb => cb({ type: 'error', error }));
        },
    };
    window.adobeIms = new AdobeIMS(imsConfig);
    window.adobeIms.initialize();

    plugin.assetUrl = `${plugin.domain}/${version}/src/assets/`; // to load images

    return plugin;
};

const plugin = getPluginEnv();

const isEnv = env => plugin.environment === env;
const isDevEnv = () => isEnv(environment.DEV);
const isStageEnv = () => isEnv(environment.STAGE);
const isProdEnv = () => isEnv(environment.PROD);
const isLocalEnv = () => plugin.isTestHarness;

// Deep Research's app per environment, like its CSP host above: the base URL new jobs run on, and the app origins KB
// may frame (KB's backend allows the same origins, from DEEP_RESEARCH_APP_ORIGINS in its own config). Dev and local
// builds get the dev app; elsewhere both are empty, so Deep Research is not configured there and stays off.
const hasDeepResearchApp = isDevEnv() || isLocalEnv();
const DEEP_RESEARCH_APP_URL = hasDeepResearchApp ? DEEP_RESEARCH_URL : '';
const DEEP_RESEARCH_APP_ORIGINS = hasDeepResearchApp ? [`https://${DEEP_RESEARCH_DOMAIN}`] : [];

export default Env;
export {
    deriveDomainsFromLocation,
    makeReady, plugin, environment, isDevEnv, isStageEnv, isProdEnv, isLocalEnv, DEEP_RESEARCH_URL,
    DEEP_RESEARCH_APP_URL, DEEP_RESEARCH_APP_ORIGINS,
};
